/**
 * The delta buffers for one live wake — SPEC §3.5 #5.
 *
 * Twelve of the fourteen wake event kinds replace what is on the page and are idempotent on replay.
 * The two that append — `thinking_delta` and `text_delta` — **carry an `offset`, and the client
 * writes AT that offset** into a per-kind buffer rather than appending.
 *
 * That is what makes a replay of the whole stream come out byte-identical, and what survives a page
 * opened mid-wake. A reconnect resumes from the LOWEST head the tab holds, so a panel that answered
 * later is sent deltas it has already applied; appending would double them, and the transcript
 * someone reads to find out why it bought would be a lie about what it thought.
 *
 * **This is the single most defect-prone line in the phase**, and e2e/stream.spec.ts tests it
 * directly: a wake interrupted mid-stream and reconnected must render byte-identically to an
 * uninterrupted run of the same shape.
 *
 * A Map from offset to text, rebuilt in offset order, is what makes that true. It is idempotent
 * under a duplicate (the same key is overwritten with the same value), order-independent (the
 * rebuild sorts), and correct with a hole in it (the hole closes when the delta arrives, and until
 * then the text simply stops where the record does).
 */

export class DeltaBuffer {
  /** kind → offset → the text written at that offset. */
  private readonly chunks = new Map<string, Map<number, string>>();

  write(kind: string, offset: number, text: string): void {
    let byOffset = this.chunks.get(kind);
    if (!byOffset) {
      byOffset = new Map();
      this.chunks.set(kind, byOffset);
    }
    byOffset.set(offset, text);
  }

  /** One kind's text as the record has it so far. */
  read(kind: string): string {
    const byOffset = this.chunks.get(kind);
    if (!byOffset) return "";
    const offsets = [...byOffset.keys()].sort((a, b) => a - b);
    let out = "";
    for (const offset of offsets) {
      const text = byOffset.get(offset) ?? "";
      if (offset === out.length) {
        out += text;
      } else if (offset < out.length) {
        // A chunk we already hold, arriving again on a replay. Writing it at its offset is a
        // no-op by construction; splicing keeps that true even if the server ever re-coalesced it.
        out = out.slice(0, offset) + text + out.slice(offset + text.length);
      } else {
        // A hole: the delta that fills it has not arrived. Stop where the record stops rather than
        // pretending the gap is not there.
        break;
      }
    }
    return out;
  }

  /** The last line of a kind, which is what a live wake's collapsed thinking line shows. */
  lastLine(kind: string): string {
    const text = this.read(kind);
    const lines = text.split("\n").filter((line) => line.trim() !== "");
    return lines.length ? lines[lines.length - 1] : "";
  }

  clear(): void {
    this.chunks.clear();
  }
}

/** One buffer per wake, because two wakes can be open at once — one live, one being read. */
const buffers = new Map<string, DeltaBuffer>();

export function bufferFor(wakeId: string): DeltaBuffer {
  let it = buffers.get(wakeId);
  if (!it) {
    it = new DeltaBuffer();
    buffers.set(wakeId, it);
  }
  return it;
}

export function forgetBuffer(wakeId: string): void {
  buffers.delete(wakeId);
}

/** A `GET …/wakes/{id}` answer's own deltas, loaded into the buffer, so D3 and D4 are one
 *  renderer reading one source. A past wake must expand exactly as it streamed. */
export function seedFromEvents(
  wakeId: string,
  events: { type: string; data: Record<string, unknown> }[],
): void {
  const buffer = bufferFor(wakeId);
  buffer.clear();
  for (const event of events) {
    if (event.type !== "thinking_delta" && event.type !== "text_delta") continue;
    const offset = typeof event.data.offset === "number" ? event.data.offset : 0;
    const text = typeof event.data.text === "string" ? event.data.text : "";
    buffer.write(event.type, offset, text);
  }
}
